// © 2025 GridStorm / Tekivex — All Rights Reserved
// Unauthorized reproduction or distribution is prohibited.
/**
 * Sentinel tools — pure, deterministic functions over a row buffer.
 *
 * The "evidence gathering" primitives the investigator calls. Dependency-free
 * and side-effect-free so they can be unit tested and (later) exposed as
 * agent / MCP tools.
 */

import type { StreamRow } from '@gridstorm/dataflow-core';

export interface ColumnStats {
  count:  number;
  mean:   number;
  stddev: number;
  min:    number;
  max:    number;
  median: number;
}

/** Numeric values of a column across the buffer (finite only). */
export function columnValues(rows: readonly StreamRow[], col: string): number[] {
  const out: number[] = [];
  for (const r of rows) {
    const v = r[col];
    if (typeof v === 'number' && Number.isFinite(v)) out.push(v);
  }
  return out;
}

/** Column names that are numeric, sampled from the last 50 rows. */
export function numericColumns(rows: readonly StreamRow[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  const sample = rows.slice(-50);
  for (const r of sample) {
    for (const k of Object.keys(r)) {
      if (k === 'id' || k === 'timestamp' || seen.has(k)) continue;
      const v = r[k];
      if (typeof v === 'number' && Number.isFinite(v)) { seen.add(k); out.push(k); }
    }
  }
  return out;
}

export function getColumnStats(rows: readonly StreamRow[], col: string): ColumnStats {
  const v = columnValues(rows, col);
  const n = v.length;
  if (n === 0) return { count: 0, mean: 0, stddev: 0, min: 0, max: 0, median: 0 };

  let sum = 0, min = Infinity, max = -Infinity;
  for (const x of v) { sum += x; if (x < min) min = x; if (x > max) max = x; }
  const mean = sum / n;

  let sq = 0;
  for (const x of v) sq += (x - mean) * (x - mean);
  const stddev = Math.sqrt(sq / n);

  const sorted = [...v].sort((a, b) => a - b);
  const median = sorted[Math.floor(sorted.length / 2)] ?? 0;

  return { count: n, mean, stddev, min, max, median };
}

/** Last n rows, optionally projected to specific columns. */
export function getRecentWindow(
  rows: readonly StreamRow[],
  n: number,
  columns?: string[],
): Record<string, unknown>[] {
  const slice = rows.slice(-n);
  if (!columns) return slice.map((r) => ({ ...r }));
  return slice.map((r) => {
    const o: Record<string, unknown> = {};
    for (const c of columns) o[c] = r[c];
    return o;
  });
}

/**
 * Pearson correlation between two numeric columns over the buffer, with the
 * pair count. Returns null if < 3 valid pairs or zero variance.
 */
export function correlationWithN(
  rows: readonly StreamRow[], colA: string, colB: string,
): { r: number; n: number } | null {
  const xs: number[] = [];
  const ys: number[] = [];
  for (const row of rows) {
    const a = row[colA], b = row[colB];
    if (typeof a === 'number' && Number.isFinite(a) && typeof b === 'number' && Number.isFinite(b)) {
      xs.push(a); ys.push(b);
    }
  }
  const n = xs.length;
  if (n < 3) return null;

  let sx = 0, sy = 0;
  for (let i = 0; i < n; i++) { sx += xs[i]!; sy += ys[i]!; }
  const mx = sx / n, my = sy / n;

  let cov = 0, vx = 0, vy = 0;
  for (let i = 0; i < n; i++) {
    const dx = xs[i]! - mx, dy = ys[i]! - my;
    cov += dx * dy; vx += dx * dx; vy += dy * dy;
  }
  if (vx === 0 || vy === 0) return null;
  const r = cov / Math.sqrt(vx * vy);
  return { r: Math.max(-1, Math.min(1, r)), n };
}

/** Pearson r only (back-compat convenience over correlationWithN). */
export function correlate(rows: readonly StreamRow[], colA: string, colB: string): number | null {
  return correlationWithN(rows, colA, colB)?.r ?? null;
}

/**
 * Is |r| meaningfully strong for this sample size? Requires n >= 20 and
 * |r| above max(0.5, 2/sqrt(n)) — a crude large-sample significance bound
 * (≈ 95% critical value) combined with a practical strength floor.
 */
export function isMeaningfulCorrelation(r: number, n: number): boolean {
  return n >= 20 && Math.abs(r) >= Math.max(0.5, 2 / Math.sqrt(n));
}

/** The column most correlated (by |r|) with `col`, or null. */
export function strongestCorrelate(
  rows: readonly StreamRow[],
  col: string,
  candidates: string[],
): { column: string; r: number; n: number } | null {
  let best: { column: string; r: number; n: number } | null = null;
  for (const c of candidates) {
    if (c === col) continue;
    const rn = correlationWithN(rows, col, c);
    if (rn === null) continue;
    if (!best || Math.abs(rn.r) > Math.abs(best.r)) best = { column: c, r: rn.r, n: rn.n };
  }
  return best;
}
